// Billing merges, it never adds. Implements spec/algorithm.md §9–§10 (and §9b: in a team, per member).
import type { Interval } from './types.ts';

const HOUR_MS = 3_600_000;

/** Union of intervals. Overlapping and touching intervals merge. Empty intervals are dropped. */
export function mergeIntervals(list: readonly Interval[]): Interval[] {
  const sorted = list
    .filter((i) => i.end > i.start)
    .map((i) => ({ start: i.start, end: i.end }))
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const out: Interval[] = [];
  for (const interval of sorted) {
    const last = out[out.length - 1];
    if (last && interval.start <= last.end) {
      if (interval.end > last.end) last.end = interval.end;
    } else {
      out.push(interval);
    }
  }
  return out;
}

export function sumIntervals(list: readonly Interval[]): number {
  return list.reduce((sum, i) => sum + Math.max(0, i.end - i.start), 0);
}

/** Clip intervals to [start, end). */
export function clipIntervals(list: readonly Interval[], start: number, end: number): Interval[] {
  const out: Interval[] = [];
  for (const i of list) {
    const s = Math.max(i.start, start);
    const e = Math.min(i.end, end);
    if (e > s) out.push({ start: s, end: e });
  }
  return out;
}

/** `round(ms × rateCents / 1 h)`, half up. `0` without a rate. */
export function amountCents(ms: number, rateCents: number | null | undefined): number {
  if (!rateCents || ms <= 0) return 0;
  return Math.floor((ms * rateCents + HOUR_MS / 2) / HOUR_MS);
}

export interface BillingProject {
  id: string;
  rateCents: number | null;
  billable: boolean;
  /** 0 (none), 1, 6 or 15. */
  roundingMinutes: number;
}

export interface BillingEntry {
  project: string | null;
  start: number;
  /** `null` = running, ends at `now`. */
  end: number | null;
  billable: boolean;
  /** The member whose time this is (§9b). Absent: the one member of a solo workspace. */
  member?: string | null;
}

export interface BillingAgentSpan {
  project: string | null;
  start: number;
  /** `null` = open, ends at `now`. */
  end: number | null;
  /** The member whose device recorded the session (§9b). */
  member?: string | null;
}

export interface ProjectTotals {
  project: string;
  humanMs: number;
  agentMs: number;
  rawMs: number;
  mergedMs: number;
  overlapMs: number;
  billableMs: number;
  amountCents: number;
  /** Union of all intervals: what the billed lane draws. In a team, one union per member (§9b). */
  merged: Interval[];
  /** Union of billable intervals: what an export bills. In a team, one union per member. */
  billableIntervals: Interval[];
}

export interface BillingInput {
  projects: readonly BillingProject[];
  entries: readonly BillingEntry[];
  agentSpans: readonly BillingAgentSpan[];
  now: number;
  /** Optional clip range, e.g. a local day. */
  range?: Interval;
}

/** Per-project totals. Each project merges on its own, and in a team each member within it (§9b); projects without any time are omitted. */
export function billProjects(input: BillingInput): Map<string, ProjectTotals> {
  const clip = (list: Interval[]): Interval[] => (input.range ? clipIntervals(list, input.range.start, input.range.end) : list.filter((i) => i.end > i.start));
  // project → member → intervals
  const human = new Map<string, Map<string, { interval: Interval; billable: boolean }[]>>();
  const agent = new Map<string, Map<string, Interval[]>>();
  const add = <T>(map: Map<string, Map<string, T[]>>, project: string, member: string, value: T) => {
    const byMember = map.get(project) ?? new Map<string, T[]>();
    const list = byMember.get(member) ?? [];
    list.push(value);
    byMember.set(member, list);
    map.set(project, byMember);
  };

  for (const entry of input.entries) {
    if (!entry.project) continue;
    const [interval] = clip([{ start: entry.start, end: entry.end ?? input.now }]);
    if (!interval) continue;
    add(human, entry.project, entry.member ?? '', { interval, billable: entry.billable });
  }
  for (const span of input.agentSpans) {
    if (!span.project) continue;
    const [interval] = clip([{ start: span.start, end: span.end ?? input.now }]);
    if (!interval) continue;
    add(agent, span.project, span.member ?? '', interval);
  }

  const result = new Map<string, ProjectTotals>();
  for (const project of input.projects) {
    const h = human.get(project.id) ?? new Map<string, { interval: Interval; billable: boolean }[]>();
    const a = agent.get(project.id) ?? new Map<string, Interval[]>();
    if (h.size === 0 && a.size === 0) continue;
    let humanMs = 0, agentMs = 0;
    const merged: Interval[] = [];
    const billableIntervals: Interval[] = [];
    for (const member of new Set([...h.keys(), ...a.keys()])) {
      const mine = h.get(member) ?? [];
      const agents = a.get(member) ?? [];
      const humanIntervals = mine.map((x) => x.interval);
      humanMs += sumIntervals(humanIntervals);
      agentMs += sumIntervals(agents);
      merged.push(...mergeIntervals([...humanIntervals, ...agents]));
      if (project.billable) billableIntervals.push(...mergeIntervals([...mine.filter((x) => x.billable).map((x) => x.interval), ...agents]));
    }
    const byStart = (x: Interval, y: Interval) => x.start - y.start || x.end - y.end;
    merged.sort(byStart);
    billableIntervals.sort(byStart);
    const mergedMs = sumIntervals(merged);
    const billableMs = sumIntervals(billableIntervals);
    result.set(project.id, {
      project: project.id,
      humanMs,
      agentMs,
      rawMs: humanMs + agentMs,
      mergedMs,
      overlapMs: humanMs + agentMs - mergedMs,
      billableMs,
      amountCents: amountCents(billableMs, project.rateCents),
      merged,
      billableIntervals,
    });
  }
  return result;
}

/** A correction of agent time (spec §9a). */
export interface SpanEdit {
  id: string;
  session: string;
  kind: 'reassign' | 'discard';
  start: number;
  end: number;
  /** Target project for `reassign`. */
  project?: string | null;
  createdAt: number;
  revertedAt?: number | null;
}

/** Agent working time of one session, attributed to a project (and, in a team, to the member whose device recorded it). */
export interface AgentPiece {
  session: string;
  project: string | null;
  start: number;
  end: number;
  member?: string | null;
}

/** Applies edits (§9a): in createdAt order, discard removes a range, reassign moves it to another project. */
export function applyEdits(pieces: readonly AgentPiece[], edits: readonly SpanEdit[]): AgentPiece[] {
  const active = edits
    .filter((e) => e.revertedAt === undefined || e.revertedAt === null)
    .filter((e) => e.end > e.start)
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  let current = pieces.map((p) => ({ ...p }));
  for (const edit of active) {
    const next: AgentPiece[] = [];
    for (const piece of current) {
      if (piece.session !== edit.session || piece.end <= edit.start || piece.start >= edit.end) {
        next.push(piece);
        continue;
      }
      if (piece.start < edit.start) next.push({ ...piece, end: edit.start });
      if (edit.kind === 'reassign') {
        next.push({ ...piece, project: edit.project ?? null, start: Math.max(piece.start, edit.start), end: Math.min(piece.end, edit.end) });
      }
      if (piece.end > edit.end) next.push({ ...piece, start: edit.end });
    }
    current = next;
  }
  return current.sort((a, b) => a.start - b.start || a.end - b.end);
}

export interface RoundedExport {
  intervals: Interval[];
  roundedMs: number;
  amountCents: number;
}

/** Export rounding (§10): each merged billable interval rounds up to the increment. */
export function roundForExport(billableIntervals: readonly Interval[], roundingMinutes: number, rateCents: number | null): RoundedExport {
  const step = roundingMinutes > 0 ? roundingMinutes * 60_000 : 0;
  let roundedMs = 0;
  for (const i of billableIntervals) {
    const duration = Math.max(0, i.end - i.start);
    roundedMs += step > 0 ? Math.ceil(duration / step) * step : duration;
  }
  return { intervals: billableIntervals.map((i) => ({ ...i })), roundedMs, amountCents: amountCents(roundedMs, rateCents) };
}
